<template>
  <div
    id="landing-page-order-configurations"
    class="max-w-screen-xl mx-auto px-4"
  >
    <h2 class="uppercase">{{ component.title }}</h2>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <landing-page-order-configuration
        v-for="(config, index) in standardConfigurations"
        :key="config.id"
        :order-configuration="config"
        :product-key="productKey"
        :product-type="productType"
        :chargebee-plan-id="chargebeePlanId"
        :pre-order="preOrder"
        :test-id="`landing-page-plan-card-${index}`"
        :selected="
          selectedConfiguration && selectedConfiguration.id === config.id
        "
        :is-discounted-game="isDiscountedGame"
        :discounted-percentage="discountedPercentage"
        @click.exact="selectConfiguration(config)"
      />

      <landing-page-custom-order-configuration
        :order-url="customOrderUrl"
        :chargebee-plan-id="chargebeePlanId"
        :pre-order="preOrder"
        :background-image="component.backgroundImage"
        test-id="landing-page-plan-card-custom"
        @click.exact="selectConfiguration(undefined)"
      />
    </div>
    <p v-if="isDiscountedGame" class="mt-4 text-right">
      * {{ $t('landingpage/discount/disclaimer') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import {
  type StrapiOrderConfigurationContent,
  type StrapiOrderConfigurations,
} from '~/apollo/types/types';
import type {
  OrderConfiguration,
  OrderConfigurationLocationPrice,
} from '~/store/types';

const props = withDefaults(
  defineProps<{
    component: StrapiOrderConfigurations;
    productKey?: string | null;
    productType: string;
    chargebeePlanId?: string | null;
    orderUrl?: string | null;
    preOrder?: boolean;
    isDiscountedGame?: boolean;
    discountedPercentage?: number;
  }>(),
  {
    productKey: null,
    chargebeePlanId: null,
    orderUrl: null,
    preOrder: false,
    isDiscountedGame: false,
    discountedPercentage: 0.0,
  },
);

const { format } = useNumberMoneyFormatting();
const orderStore = useOrderStore();
const locationStore = useLocationStore();

const getSelectedLocationInfo = (
  prices: Array<OrderConfigurationLocationPrice & { orderUrl: string }>,
): { slug: string; orderUrl: string } => {
  const findLocationBySlug = (slug: string) =>
    prices.find((l) => l.slug === slug);

  if (locationStore.selectedLocation.slug != null) {
    const found = findLocationBySlug(locationStore.selectedLocation.slug);
    if (found) return { slug: found.slug, orderUrl: found.orderUrl };
  }

  if (locationStore.bestLatencyLocation != null) {
    const found = findLocationBySlug(locationStore.bestLatencyLocation);
    if (found) return { slug: found.slug, orderUrl: found.orderUrl };
  }

  if (prices.length > 0) {
    const { slug, orderUrl } = prices[0];
    return { slug, orderUrl };
  }

  return null;
};

const selectConfiguration = (sc?: StrapiOrderConfigurationContent) => {
  if (sc == null) {
    orderStore.setSelectedOrderConfiguration();
  } else {
    const prices: Array<
      OrderConfigurationLocationPrice & { orderUrl: string }
    > = sc.locations
      .filter((loc) => loc.prices.length > 0)
      .map((loc) => {
        const locPrice = loc.prices[0];
        const isConversion =
          locPrice.formatted && locPrice.formatted.includes(' (~');
        return {
          orderUrl: loc.orderUrl,
          slug: loc.slug,
          rawPrice: {
            amount: locPrice.value,
            currencyCode: locPrice.currency,
            fraction: locPrice.currency === 'JPY' ? 0 : 2,
          },
          price: isConversion
            ? locPrice.formatted
            : format({
                amount: locPrice.value,
                currencyCode: locPrice.currency,
                fraction: locPrice.currency === 'JPY' ? 0 : 2,
              }),
        };
      });

    const { slug, orderUrl } = getSelectedLocationInfo(prices);
    locationStore.setLocation({ slug, orderUrl });

    orderStore.setSelectedOrderConfiguration({
      prices,
      id: sc.id,
      duration: sc.duration,
      units: sc.units,
      unitType: sc.unitType,
    });
  }
};

const selectedConfiguration = computed<OrderConfiguration | undefined>(
  () => orderStore.selectedOrderConfiguration,
);

/** Last up-to-3 standard plans (same slice used in the template). */
const standardConfigurations = computed(() =>
  props.component.orderConfigurations.slice(-3),
);

const customOrderUrl = computed(() => {
  const slugToKey = new Map<string, string>();
  for (const oc of props.component.orderConfigurations) {
    for (const loc of oc.locations) {
      if (!slugToKey.has(loc.slug)) slugToKey.set(loc.slug, loc.key);
    }
  }

  let locationKey: string | undefined;
  if (locationStore.bestLatencyLocation) {
    locationKey = slugToKey.get(locationStore.bestLatencyLocation);
  }
  if (!locationKey && locationStore.selectedLocation.slug) {
    locationKey = slugToKey.get(locationStore.selectedLocation.slug);
  }

  if (!locationKey) return props.orderUrl;

  const separator = props.orderUrl.includes('?') ? '&' : '?';
  return `${props.orderUrl}${separator}loc=${locationKey}`;
});

onUnmounted(() => {
  selectConfiguration();
});
</script>
